import { Button } from "@/components/ui/button";
import { SEO } from "@/components/SEO";
import { Input } from "@/components/ui/input";

import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useLanguage } from "@/contexts/LanguageContext";
import { Send, Mail, MapPin } from "lucide-react";
import { PageHero } from "@/components/shared/PageHero";
import { GlowCard } from "@/components/shared/GlowCard";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";

const Contact = () => {
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { t } = useLanguage();
  const { ref } = useRevealOnScroll<HTMLDivElement>();

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    const form = e.currentTarget;
    const formData = new FormData(form);
    const data = {
      name: formData.get("name") as string,
      email: formData.get("email") as string,
      message: formData.get("message") as string,
    };
    try {
      const { error } = await supabase.functions.invoke("send-contact-email", { body: data });
      if (error) throw error;
      toast({ title: t("messageSentTitle"), description: t("messageSentDesc") });
      form.reset();
    } catch (error) {
      console.error("Error sending message:", error);
      toast({ title: t("messageErrorTitle"), description: t("messageErrorDesc"), variant: "destructive" });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="relative min-h-screen">
      <SEO
        title="Contact Fusion Media — Get in Touch"
        description="Reach Fusion Media's team in Riyadh, Saudi Arabia. Email support, sales inquiries, and partnership requests."
      />

      <div className="relative max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8">
        <PageHero
          eyebrow={t("getInTouch")}
          title={t("contactUsTitle")}
          subtitle={t("contactUsDesc")}
          centered
        />

        <section ref={ref} className="reveal-on-scroll pb-12 md:pb-16">
          <div className="grid gap-6 md:grid-cols-2 max-w-3xl mx-auto">
            <GlowCard icon={Mail} accent="blue" title={t("email")} description="Support@Fusion-Media.sa" />
            <GlowCard icon={MapPin} accent="violet" title={t("location")} description="Riyadh, Saudi Arabia" />
          </div>
        </section>

        <section className="pb-16 md:pb-24">
          <div className="relative max-w-3xl mx-auto bg-card border border-border/60 rounded-2xl p-6 md:p-10 shadow-card overflow-hidden">
            <div
              className="absolute -top-12 -right-12 w-48 h-48 rounded-full blur-3xl pointer-events-none"
              style={{ background: "hsl(217 91% 60% / 0.15)" }}
            />
            <div className="relative">
              <div className="text-center mb-8">
                <div className="w-14 h-14 mx-auto rounded-2xl bg-gradient-to-br from-primary to-primary-light flex items-center justify-center shadow-lg mb-4">
                  <Send className="w-7 h-7 text-white" />
                </div>
                <h2 className="text-2xl md:text-3xl font-bold tracking-tight text-foreground mb-2">
                  {t("sendMessage")}
                </h2>
                <p className="text-muted-foreground">{t("readyToStart")}</p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid gap-6 md:grid-cols-2">
                  <div className="space-y-2">
                    <label htmlFor="name" className="block text-sm font-semibold text-foreground">
                      {t("name")}
                    </label>
                    <Input id="name" name="name" required placeholder={t("yourName")} className="h-12" />
                  </div>
                  <div className="space-y-2">
                    <label htmlFor="email" className="block text-sm font-semibold text-foreground">
                      {t("contactEmail")}
                    </label>
                    <Input id="email" name="email" type="email" required placeholder={t("yourEmail")} className="h-12" />
                  </div>
                </div>
                <div className="space-y-2">
                  <label htmlFor="message" className="block text-sm font-semibold text-foreground">
                    {t("message")}
                  </label>
                  <Textarea id="message" name="message" required placeholder={t("howCanWeHelp")} rows={6} />
                </div>
                <div className="flex justify-center">
                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="h-12 px-8 rounded-full bg-gradient-to-r from-primary to-primary-light text-primary-foreground shadow-lg shadow-primary/30 hover:shadow-xl hover:shadow-primary/40 hover:-translate-y-0.5 transition-all"
                  >
                    <Send className="w-4 h-4 mr-2" />
                    {isSubmitting ? t("sending") : t("send")}
                  </Button>
                </div>
              </form>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default Contact;
